import { formatPesoAmount } from './money';
export const COMPLEX_PRICE_RANGE_MODES = Object.freeze([
  'markup_percent',
  'markup_amount',
]);

export const createEmptyComplexPriceRuleDraft = () => ({
  name: '',
  ranges: [{
    id: null,
    maxCost: null,
    mode: 'markup_percent',
    value: '0',
  }],
});

const inputValue = (value) => (
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? ''
    : String(value)
);

export const normalizeComplexPriceRuleDraft = (rule) => {
  if (!rule) return createEmptyComplexPriceRuleDraft();
  const sourceRanges = Array.isArray(rule.ranges) ? rule.ranges : [];
  const ranges = sourceRanges
    .slice()
    .sort((left, right) => Number(left?.sortOrder || 0) - Number(right?.sortOrder || 0))
    .map((range, index) => ({
      id: range?.id ?? null,
      maxCost: index === sourceRanges.length - 1
        ? null
        : inputValue(range?.maxCost ?? range?.upperCost ?? range?.upperBound),
      mode: COMPLEX_PRICE_RANGE_MODES.includes(range?.mode)
        ? range.mode
        : 'markup_percent',
      value: inputValue(range?.value),
    }));
  return {
    id: rule.id ?? null,
    name: String(rule.name || ''),
    ranges: ranges.length ? ranges : createEmptyComplexPriceRuleDraft().ranges,
  };
};

const parseStrictNumber = (value, { integer = false } = {}) => {
  const normalized = String(value ?? '').trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0 || (integer && !Number.isInteger(parsed))) {
    return null;
  }
  return parsed;
};

export const validateComplexPriceRuleDraft = (draft) => {
  const name = String(draft?.name || '').trim();
  if (!name) return { error: 'Ingresa un nombre para la regla.', field: 'name' };
  const ranges = Array.isArray(draft?.ranges) ? draft.ranges : [];
  if (!ranges.length) return { error: 'Agrega al menos un rango.', field: 'ranges' };

  let minCost = 0;
  const normalizedRanges = [];
  for (let index = 0; index < ranges.length; index += 1) {
    const range = ranges[index] || {};
    if (!COMPLEX_PRICE_RANGE_MODES.includes(range.mode)) {
      return { error: `Selecciona un tipo válido para el rango ${index + 1}.`, field: `ranges.${index}.mode` };
    }
    const integerValue = range.mode === 'markup_amount';
    const value = parseStrictNumber(range.value, { integer: integerValue });
    if (value === null) {
      return {
        error: integerValue
          ? `Ingresa un monto entero válido para el rango ${index + 1}.`
          : `Ingresa un porcentaje válido para el rango ${index + 1}.`,
        field: `ranges.${index}.value`,
      };
    }
    let maxCost = null;
    if (index < ranges.length - 1) {
      maxCost = parseStrictNumber(range.maxCost, { integer: true });
      if (maxCost === null || maxCost <= minCost) {
        return {
          error: `El límite superior del rango ${index + 1} debe ser un entero mayor que ${minCost}.`,
          field: `ranges.${index}.maxCost`,
        };
      }
    }
    normalizedRanges.push({
      sortOrder: index,
      minCost,
      maxCost,
      mode: range.mode,
      value,
    });
    if (maxCost !== null) minCost = maxCost;
  }
  return { payload: { name, ranges: normalizedRanges } };
};

export const applyComplexRange = (cost, range) => {
  const numericCost = Number(cost);
  const value = Number(range?.value);
  if (!Number.isFinite(numericCost) || numericCost < 0 || !Number.isFinite(value) || value < 0) {
    return null;
  }
  return range?.mode === 'markup_percent'
    ? numericCost * (1 + value / 100)
    : numericCost + value;
};

export const normalizePriceRuleSelection = (selection, legacySimpleRule = null) => {
  if (selection?.type === 'complex') {
    const complexPriceRuleId = Number(selection.complexPriceRuleId);
    return Number.isSafeInteger(complexPriceRuleId) && complexPriceRuleId > 0
      ? { type: 'complex', complexPriceRuleId }
      : null;
  }
  const simpleRule = selection?.type === 'simple' ? selection.rule : legacySimpleRule;
  if (!simpleRule || typeof simpleRule !== 'object') return null;
  if (simpleRule.value === null || simpleRule.value === undefined
    || !['number', 'string'].includes(typeof simpleRule.value)
    || String(simpleRule.value).trim() === '') return null;
  const value = Number(simpleRule.value);
  if (!['markup_amount', 'markup_percent', 'fixed_price'].includes(simpleRule.mode)
    || !Number.isFinite(value) || value < 0) return null;
  return {
    type: 'simple',
    rule: {
      mode: simpleRule.mode,
      value,
      ...(simpleRule.basis ? { basis: simpleRule.basis } : {}),
    },
  };
};

export const hasPriceRuleSelection = (selection, legacySimpleRule = null) => (
  Boolean(normalizePriceRuleSelection(selection, legacySimpleRule))
);

export const findComplexPriceRule = (rules, complexPriceRuleId) => (
  (Array.isArray(rules) ? rules : []).find(
    (rule) => String(rule?.id) === String(complexPriceRuleId)
  ) || null
);

export const findMatchingComplexRange = (rule, referenceCost) => {
  const cost = Number(referenceCost);
  if (referenceCost === null || referenceCost === undefined || referenceCost === ''
    || !Number.isFinite(cost) || cost < 0) return null;
  return (Array.isArray(rule?.ranges) ? rule.ranges : []).find((range) => {
    const minCost = Number(range?.minCost);
    const maxCost = range?.maxCost === null || range?.maxCost === undefined
      ? null
      : Number(range.maxCost);
    return Number.isFinite(minCost)
      && cost >= minCost
      && (maxCost === null || (Number.isFinite(maxCost) && cost < maxCost));
  }) || null;
};

export const resolvePriceRuleSelection = (
  selection,
  { complexPriceRules = [], referenceCost = null, basis = 'unit' } = {}
) => {
  const normalized = normalizePriceRuleSelection(selection);
  if (!normalized) return {
    selection: null,
    complexPriceRule: null,
    matchedRange: null,
    resolvedRule: null,
  };
  if (normalized.type === 'simple') return {
    selection: normalized,
    complexPriceRule: null,
    matchedRange: null,
    resolvedRule: normalized.rule,
  };
  const complexPriceRule = findComplexPriceRule(
    complexPriceRules,
    normalized.complexPriceRuleId
  );
  const matchedRange = findMatchingComplexRange(complexPriceRule, referenceCost);
  return {
    selection: normalized,
    complexPriceRule,
    matchedRange,
    resolvedRule: matchedRange ? {
      mode: matchedRange.mode,
      value: Number(matchedRange.value),
      basis: basis === 'kg' ? 'kg' : 'unit',
    } : null,
  };
};

export const getComplexRulePreview = (draft) => {
  const validation = validateComplexPriceRuleDraft(draft);
  if (!validation.payload) return { datasets: [], maxCost: 10000, error: validation.error };
  const ranges = validation.payload.ranges;
  const finiteMaxima = ranges.map((range) => range.maxCost).filter(Number.isFinite);
  const largestBoundary = finiteMaxima.length ? Math.max(...finiteMaxima) : 0;
  const maxCost = Math.max(10000, Math.ceil(largestBoundary * 1.25));
  const colors = ['#2563eb', '#7c3aed', '#0891b2', '#16a34a', '#d97706', '#dc2626'];
  const datasets = ranges.map((range, index) => {
    const end = range.maxCost ?? maxCost;
    return {
      label: `Rango ${index + 1}`,
      data: [
        { x: range.minCost, y: applyComplexRange(range.minCost, range) },
        { x: end, y: applyComplexRange(end, range) },
      ],
      borderColor: colors[index % colors.length],
      backgroundColor: colors[index % colors.length],
      pointRadius: 3,
      tension: 0,
    };
  });
  const breakpoints = ranges.slice(1).flatMap((range, index) => {
    const previousRange = ranges[index];
    const x = range.minCost;
    const before = applyComplexRange(x, previousRange);
    const after = applyComplexRange(x, range);
    return before === after ? [{ x, y: after }] : [{ x, y: before }, { x, y: after }];
  });
  if (breakpoints.length) {
    datasets.push({
      label: 'Puntos de quiebre',
      data: breakpoints,
      borderColor: '#0f172a',
      backgroundColor: '#ffffff',
      pointBorderWidth: 2,
      pointRadius: 5,
      pointStyle: 'rectRot',
      showLine: false,
    });
  }
  datasets.push({
    label: 'Costo sin margen',
    data: [{ x: 0, y: 0 }, { x: maxCost, y: maxCost }],
    borderColor: '#94a3b8',
    backgroundColor: '#94a3b8',
    borderDash: [6, 5],
    pointRadius: 0,
    tension: 0,
  });
  return { datasets, maxCost, error: null };
};

export const formatComplexRangeLabel = (range, index = 0) => {
  const minCost = formatPesoAmount(Number(range?.minCost || 0));
  const maxCost = range?.maxCost === null || range?.maxCost === undefined
    ? '∞'
    : formatPesoAmount(Number(range.maxCost));
  const value = range?.mode === 'markup_percent'
    ? Number(range?.value || 0).toLocaleString('es-CL')
    : formatPesoAmount(range?.value || 0);
  const action = range?.mode === 'markup_percent' ? `${value}%` : `$${value}`;
  return `Rango ${index + 1}: $${minCost} ≤ costo < ${maxCost === '∞' ? maxCost : `$${maxCost}`} · ${action}`;
};
